import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { Edit, Trash2, Eye } from "lucide-react";
import "./AllStaff.css";

const API_BASE = "https://server.account.majestyfx.com";

export default function AllStaff() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(""); // 'edit' or 'view'
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({
    admin_id: "",
    email: "",
    password: "",
    roleType: "",
  });

  useEffect(() => {
    fetchStaff();
    fetchRoles();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/admin_fetch_adminroles_data.php`);
      if (response.data.success) {
        setStaff(response.data.data || []);
      } else {
        setError("Failed to fetch staff data");
      }
    } catch (err) {
      setError("Error fetching staff: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin_fetch_roles.php`);
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      admin_id: staffMember.id,
      email: staffMember.email,
      password: "",
      roleType: staffMember.role_name,
    });
    setModalMode("edit");
    setShowModal(true);
    setError("");
    setSuccess("");
  };

  const handleView = (staffMember) => {
    setSelectedStaff(staffMember);
    setModalMode("view");
    setShowModal(true);
  };

  const handleDelete = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/delete_admin.php`, {
        admin_id: staffId,
      });

      if (response.data.success) {
        setSuccess("Staff deleted successfully");
        fetchStaff();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data.message || "Failed to delete staff");
      }
    } catch (err) {
      setError("Error deleting staff: " + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.email || !formData.roleType) {
      setError("Email and Role are required");
      return;
    }

    try {
      const payload = {
        admin_id: formData.admin_id,
        email: formData.email,
        roleType: formData.roleType,
      };

      // Only include password if it's provided
      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await axios.post(`${API_BASE}/admin_update_roles_main.php`, payload);

      if (response.data.success) {
        setSuccess("Staff updated successfully");
        fetchStaff();
        setTimeout(() => {
          setShowModal(false);
          setSuccess("");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to update staff");
      }
    } catch (err) {
      setError("Error updating staff: " + err.message);
    }
  };

  const handleStatusToggle = async (staffId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const response = await axios.post(`${API_BASE}/admin_toggle_status.php`, {
        admin_id: staffId,
        status: newStatus,
      });

      if (response.data.success) {
        setSuccess("Status updated successfully");
        fetchStaff();
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      setError("Error updating status: " + err.message);
    }
  };

  const filteredStaff = staff.filter(
    (member) =>
      member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.role_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="ib-clients-page">
      {/* Page Title + Breadcrumb */}
      <div className="page-header">
        <h2>Staff List</h2>
        <div className="breadcrumb">
          <a href="/">Home</a> / View All
        </div>
      </div>

      {/* Card Container */}
      <div className="card">
        {/* Card Header with "Create New" button */}
        <div className="card-header">
          <h3>View all staff</h3>
          <div className="header-buttons">
            <NavLink to="/new-staff" className="btn blue">
              + Create New
            </NavLink>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Table Controls */}
        <div className="table-controls">
        

          <div className="search">
            <label>
              Search:{" "}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
          </div>
        </div>

        {/* ✅ Responsive Table Wrapper */}
        <div className="table-responsive">
          {loading ? (
            <div className="loading">Loading staff data...</div>
          ) : (
            <table className="ib-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Employee ID</th>
                  <th>Role</th>
                  {/* <th>Access Type</th> */}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((member, index) => (
                    <tr key={member.id}>
                      <td>{index + 1}</td>
                      <td>{member.username}</td>
                      <td>{member.email}</td>
                      <td>{member.empId}</td>
                      <td>{member.role_name}</td>
                      {/* <td>
                        <span className={`badge ${member.access_type}`}>
                          {member.access_type}
                        </span>
                      </td> */}
                      <td>
                        <button
                          className={`status-toggle ${member.status}`}
                          // onClick={() => handleStatusToggle(member.id, member.status)}
                        >
                          {member.status === "active" ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn view"
                            onClick={() => handleView(member)}
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleEdit(member)}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDelete(member.id, member.username)}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="table-footer">
          <div>
            Showing {filteredStaff.length > 0 ? 1 : 0} to {filteredStaff.length} of{" "}
            {filteredStaff.length} entries
          </div>
          <div className="pagination">
            <button disabled>Previous</button>
            <button disabled>Next</button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && modalMode === "edit" && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Staff Member</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                <div className="form-group">
                  <label>
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Enter new password or leave blank"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Role <span className="required">*</span>
                  </label>
                  <select
                    value={formData.roleType}
                    onChange={(e) =>
                      setFormData({ ...formData, roleType: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Update Staff
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showModal && modalMode === "view" && selectedStaff && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Staff Details</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item">
                  <strong>Username:</strong>
                  <span>{selectedStaff.username}</span>
                </div>
                <div className="detail-item">
                  <strong>Email:</strong>
                  <span>{selectedStaff.email}</span>
                </div>
                <div className="detail-item">
                  <strong>Employee ID:</strong>
                  <span>{selectedStaff.empId}</span>
                </div>
                <div className="detail-item">
                  <strong>Role:</strong>
                  <span>{selectedStaff.role_name}</span>
                </div>
                {/* <div className="detail-item">
                  <strong>Access Type:</strong>
                  <span className={`badge ${selectedStaff.access_type}`}>
                    {selectedStaff.access_type}
                  </span>
                </div> */}
                <div className="detail-item">
                  <strong>Status:</strong>
                  <span className={`status-badge ${selectedStaff.status}`}>
                    {selectedStaff.status}
                  </span>
                </div>
                <div className="detail-item full-width">
                  <strong>Role Description:</strong>
                  <span>{selectedStaff.role_description || "N/A"}</span>
                </div>
              </div>

              {/* Permissions Section */}
              <div className="permissions-section">
                <h4>Permissions</h4>
                {Array.isArray(selectedStaff.permissions) ? (
                  <div className="permissions-list">
                    {selectedStaff.permissions.map((permission) => (
                      <div key={permission.id} className="permission-badge">
                        <strong>{permission.name}</strong>
                        <span className="permission-module">{permission.module}</span>
                        <small>{permission.description}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="full-access-note">{selectedStaff.permissions}</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

